// Background-tab alerting for incoming calls.
// Uses the Web Notifications API (system-level) and document title flashing
// so agents are alerted even when the browser tab is hidden or behind other windows.
// Also provides cross-tab audio deduplication via localStorage so only one tab rings.

let activeNotification: Notification | null = null
let titleFlashInterval: ReturnType<typeof setInterval> | null = null
let originalTitle: string = ""

// Cross-tab audio lock — prevents multiple tabs from playing ringtone simultaneously.
// Each tab writes its ID + timestamp to localStorage. A tab only plays if it holds the
// lock (its own ID) or the lock is stale (holder tab closed/crashed).
const AUDIO_LOCK_KEY = "incoming-call-audio-tab"
const AUDIO_LOCK_TTL = 5000 // ms — lock expires if not refreshed within this window
const TAB_ID = Math.random().toString(36).slice(2)

export function claimAudioLock(): boolean {
  try {
    const stored = localStorage.getItem(AUDIO_LOCK_KEY)
    if (stored) {
      const { tabId, timestamp } = JSON.parse(stored)
      if (tabId !== TAB_ID && Date.now() - timestamp < AUDIO_LOCK_TTL) {
        return false // another tab is actively playing
      }
    }
    localStorage.setItem(AUDIO_LOCK_KEY, JSON.stringify({ tabId: TAB_ID, timestamp: Date.now() }))
    return true
  } catch {
    return true // fallback: play on localStorage error
  }
}

export function releaseAudioLock(): void {
  try {
    const stored = localStorage.getItem(AUDIO_LOCK_KEY)
    if (stored) {
      const { tabId } = JSON.parse(stored)
      if (tabId === TAB_ID) {
        localStorage.removeItem(AUDIO_LOCK_KEY)
      }
    }
  } catch {
    // ignore
  }
}

export function isNotificationSupported(): boolean {
  return "Notification" in window
}

export function requestNotificationPermission(): void {
  if (!isNotificationSupported()) return
  if (Notification.permission === "default") {
    Notification.requestPermission()
  }
}

export function showIncomingCallAlert(callerName: string, callerNumber: string): void {
  const displayName = callerName || callerNumber || "Unknown Caller"

  // Browser notification
  if (isNotificationSupported() && Notification.permission === "granted") {
    try {
      activeNotification = new Notification("Incoming Call", {
        body: callerName ? `${callerName} - ${callerNumber}` : callerNumber || "Unknown Caller",
        icon: "/favicon.png",
        tag: "incoming-call",
        requireInteraction: true,
      })
      console.log("[incoming-call-alerts] Notification created", activeNotification)
      activeNotification.onclick = () => {
        window.focus()
        activeNotification?.close()
        activeNotification = null
      }
      activeNotification.onerror = (e) => {
        console.error("[incoming-call-alerts] Notification error", e)
      }
    } catch (e) {
      console.error("[incoming-call-alerts] Notification constructor failed", e)
    }
  }

  // Title flash — works without any permissions
  if (!titleFlashInterval) {
    originalTitle = document.title
    let showAlert = true
    titleFlashInterval = setInterval(() => {
      document.title = showAlert ? `INCOMING CALL - ${displayName}` : originalTitle
      showAlert = !showAlert
    }, 1000)
  }
}

export function dismissIncomingCallAlert(): void {
  if (activeNotification) {
    activeNotification.close()
    activeNotification = null
  }

  if (titleFlashInterval) {
    clearInterval(titleFlashInterval)
    titleFlashInterval = null
    document.title = originalTitle
  }

  releaseAudioLock()
}
